import { useState } from 'react'
import { useAuth } from '../../state/auth'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { Card } from '../../components/ui/card'
import { motion } from 'framer-motion'
import type { LoginRequest } from '../../models/auth'
import { useTranslation } from '../../hooks/useTranslation'
import { useMutationTracker } from "@/hooks/useMutationTracker";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog'
import { AlertTriangle, Eye, EyeOff } from 'lucide-react'
import { useDesignSystem } from '@/hooks/useDesignSystem'
import { ParticleNetwork } from '@/components/ui/particle-network'
import { Link } from '@tanstack/react-router'

const getErrorMessage = (error: unknown): string => {
  if (!error) return ''
  if (typeof error === 'string') return error
  if (error && typeof error === 'object' && 'message' in error) {
    return String(error.message)
  }
  return 'An error occurred'
}

export default function LoginPage() {
  const { login, loginLoading, loginError } = useAuth()
  const { t } = useTranslation()
  const { animations, patterns } = useDesignSystem()
  const [formData, setFormData] = useState<LoginRequest>({
    email: '',
    password: ''
  })
  const [showPassword, setShowPassword] = useState(false)
  const [showErrorDialog, setShowErrorDialog] = useState(false)
  
  const stater = useMutationTracker('login')
  stater.addListener(({isSuccess}) => {
    if(isSuccess){
      window.location.href = '/dashboard'
    } else if (loginError) {
      setShowErrorDialog(true)
    }
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    login(formData)
  }

  const handleChange = (field: keyof LoginRequest) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }))
  }

  return (
    <div className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Particle Network Background */}
      <div className="absolute inset-0 z-0">
        <ParticleNetwork className="w-full h-full" />
      </div>

      {/* Dark overlay for better contrast */}
      <div className="absolute inset-0 bg-[#080c15]/60 z-[1]" />

      <div className="relative z-10 w-full flex items-center justify-center p-6">
        <motion.div
          variants={animations.page.scaleFade}
          initial="initial"
          animate="animate"
          className="w-full max-w-md"
        >
          <Card className={'p-8 bg-[#0a0f1c]/90 border-[#0dcbfc]/20 backdrop-blur-md shadow-[0_0_50px_-12px_rgba(13,203,252,0.25)]'}>
            <motion.div
              variants={animations.content.section}
              initial="hidden"
              animate="visible"
              className="text-center mb-8"
            >
              <div className={'inline-flex items-center justify-center w-16 h-16 mb-4 shadow-[0_0_30px_-5px_rgba(13,203,252,0.5)] ' + patterns.utils.cx(patterns.radius.xl, 'bg-gradient-to-r from-[#0dcbfc] to-[#0dcbfc]/80 text-white overflow-hidden')}>
                <img src="/nexa-logo.jpg" alt="Nexa" className="w-full h-full object-cover" />
              </div>
              <h1 className={'text-3xl font-bold tracking-tight mb-2 text-white'}>{t('pages.auth.login.title')}</h1>
              <p className="text-[#0dcbfc]/70">{t('pages.auth.login.subtitle')}</p>
            </motion.div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <motion.div
                variants={animations.component.listItem}
                initial="hidden"
                animate="visible"
              >
                <Input
                  type="email"
                  placeholder={t('pages.auth.login.emailPlaceholder')}
                  value={formData.email}
                  onChange={handleChange('email')}
                  required
                  className="bg-[#080c15]/50 border-[#0dcbfc]/30 text-white placeholder:text-[#0dcbfc]/40 focus:border-[#0dcbfc] focus:ring-[#0dcbfc]/20"
                />
              </motion.div>
              
              <motion.div
                variants={animations.component.listItem}
                initial="hidden"
                animate="visible"
                className="relative"
              >
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder={t('pages.auth.login.passwordPlaceholder')}
                  value={formData.password}
                  onChange={handleChange('password')}
                  required
                  className="bg-[#080c15]/50 border-[#0dcbfc]/30 text-white placeholder:text-[#0dcbfc]/40 focus:border-[#0dcbfc] focus:ring-[#0dcbfc]/20 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#0dcbfc]/60 hover:text-[#0dcbfc] transition-colors"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </motion.div>

              <motion.div
                variants={animations.content.section}
                initial="hidden"
                animate="visible"
              >
                <Button 
                  type="submit" 
                  size="lg"
                  className="w-full font-semibold bg-[#0dcbfc] hover:bg-[#0dcbfc]/90 text-[#080c15] shadow-[0_0_20px_-5px_rgba(13,203,252,0.4)] hover:shadow-[0_0_30px_-5px_rgba(13,203,252,0.6)] transition-all"
                  disabled={loginLoading}
                >
                  {loginLoading ? (
                    <div className="flex items-center space-x-2">
                      <div className="animate-spin w-4 h-4 border-2 border-[#080c15] border-t-transparent rounded-full" />
                      <span>{t('common.messages.loading')}</span>
                    </div>
                  ) : (
                    t('pages.auth.login.signIn')
                  )}
                </Button>
              </motion.div>
            </form>

            <motion.div
              variants={animations.page.fadeIn}
              initial="initial"
              animate="animate"
              className="mt-6 text-center"
            >
              <p className="text-sm text-[#0dcbfc]/60">
                {t('pages.auth.login.noAccount')}{' '}
                <Link to="/register" className="text-[#0dcbfc] hover:text-[#0dcbfc]/80 font-medium transition-colors underline-offset-4 hover:underline">
                  {t('pages.auth.login.signUp')}
                </Link>
              </p>
            </motion.div>
          </Card>
        </motion.div>

        {/* Error Dialog */}
        <Dialog open={showErrorDialog} onOpenChange={setShowErrorDialog}>
          <DialogContent className="bg-[#0a0f1c] border-[#0dcbfc]/20 text-white">
            <DialogHeader className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center w-12 h-12 rounded-full bg-red-500/20">
                  <AlertTriangle className="w-6 h-6 text-red-400" />
                </div>
                <div>
                  <DialogTitle className="text-lg font-semibold text-white">
                    {t('common.messages.errorTitle') || 'Login Error'}
                  </DialogTitle>
                  <DialogDescription className="text-sm text-[#0dcbfc]/60 mt-1">
                    {t('pages.auth.login.errorDescription') || 'An issue occurred during sign-in.'}
                  </DialogDescription>
                </div>
              </div>
            </DialogHeader>
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
              <p className="text-sm text-red-400">
                {getErrorMessage(loginError) || t('common.messages.genericError')}
              </p>
            </div>
            <DialogFooter>
              <Button
                variant="destructive"
                onClick={() => setShowErrorDialog(false)}
                className="w-full bg-red-500/20 hover:bg-red-500/30 text-red-400 border-red-500/30"
              >
                {t('common.actions.close') || 'Close'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  )
}
